import React from 'react';

import SyntaxHighlighter from "react-syntax-highlighter";

function Implementation() {
  return (
    <div id="Implementation" className="row">
      <h3>Implementation</h3>
      <div className="w-100">
        <p>
          Our reservoir consists of <code>n_units</code>, representing the number neurons in the network.
          We use <code>numpy</code> to generate our random state.
        </p>
      </div>
      <SyntaxHighlighter className="w-100" language={ "python" }>
      {`
        def __init__(self, n_in, n_fb, n_units, spectral_radius=1.5, noise=None):
          self.n_in = n_in # input dimension
          self.n_fb = n_fb # feedback dimension
          self.n_units = n_units # reservoir units
          self.random = np.random.RandomState() # random generator
          self.spectral_radius = spectral_radius # desired spectral radius
          self.noise = noise # noise
      `}
      </SyntaxHighlighter>
      <div className="w-100">
        <p>
          We generate a random <code>n_unit</code> by <code>n_unit</code> matrix, representing the connections between our neurons.
          Since our random generator produces values between <code>0</code> and <code>1</code>, we shift the matrix <code>0.5</code> to the left.
          We also need to generate a random <code>n_units</code> by <code>n_in</code> matrix <code>w_in</code> as the input interface and a random <code>n_unit</code> by <code>n_fb</code> matrix <code>w_fb</code> as the feedback interface.
          Since our activation function is <code>tanh()</code>, we initialize the matrices with values between <code>-1.00</code> and <code>1.00</code>.
        </p>
      </div>
      <h5>Reservoir Connections</h5>
      <div className="w-100">
        <p>
          Our reservoir consists of <code>n_units</code>, representing the connections between our neurons.
          We initialize a randomly distributed matrix with values between <code>-0.5</code> and <code>0.5</code> and rebalance our matrix for our experimental spectral radius.
        </p>
      </div>
      <h5>Input Interface</h5>
      <div className="w-100">
        <p>
          A matrix of <code>n_units</code> by <code>n_in</code> shape is needed as the input for our reservoir matrix.
          Since our activation function is <code>tanh()</code>, we initialize the matrix with values between <code>-1.00</code> and <code>1.00</code>.
        </p>
      </div>
      <h5>Feedback Interface</h5>
      <div className="w-100">
        <p>
          A matrix matching our output dimensions of <code>n_units</code> by <code>n_fb</code> shape is needed as the feedback for our reservoir matrix.
          Again, we initialize the matrix with values between <code>-1.00</code> and <code>1.00</code>.
        </p>
      </div>
      <SyntaxHighlighter className="w-100" language={ "python" }>
      {`
        def _init_res(self):
          self.w = self.random.rand(self.n_units, self.n_units) - 0.5
          self.w = self.w * (self.spectral_radius / np.max(np.abs(np.linalg.eigvals(self.w))))
          self.w_in = self.random.rand(self.n_units, self.n_in) * 2 - 1
          self.w_fb = self.random.rand(self.n_units, self.n_fb) * 2 - 1
      `}
      </SyntaxHighlighter>
      <h5>State Update Equation</h5>
      <div className="w-100">
        <p>
          We implement the proposed state update equation.
        </p>
      </div>
      <SyntaxHighlighter className="w-100" language={ "python" }>
      {`
        def _next(self, state, input, feedback):
          pre = (np.dot(self.w_in, input) + np.dot(self.w, state) + np.dot(self.w_fb, feedback))
          if self.noise:
            return (np.tanh(pre) + (self.noise * (self.random.rand(self.n_units)  - 0.5)))
          else:
            return np.tanh(pre)
      `}
      </SyntaxHighlighter>
      <h5>Fitting</h5>
      <div className="w-100">
        <p>
          To harvest our reservoir states, we ratchet the reservoir state forward using our state update equation.
          Past and present state are required, so we start the fitting process from the second entry in our data.
          We choose to not implement transient states, as our data is cleaned and truncated before consumption.
        </p>
        <p>
          After harvesting, we staack our collected states with our input vector to obtain our extended system states <code>xstates</code>.
          Next, we obtain our output matrix <code>w_out</code> by aligning our calculated state collection matrix psuedoinverse with our feedback data.
          We also save our last input, state, and feedback entries as the starting step for our prediction phase.
        </p>
      </div>
      <SyntaxHighlighter className="w-100" language={ "python" }>
      {`
        def fit(self, input, feedback):
          input = np.reshape(input, (len(input), -1)) # reshape one dimensional input
          feedback = np.reshape(feedback, (len(input), -1)) # reshape one dimensional feedback
          states = np.zeros((input.shape[0], self.n_units))
          for n in range(1, input.shape[0]):
            states[n] = self._next(states[n-1], input[n], feedback[n-1])
          xstates = np.hstack((states, input))
          self.w_out = np.dot(np.linalg.pinv(xstates), feedback).T
          self.last = [input[-1], states[-1], feedback[-1]]
      `}
      </SyntaxHighlighter>
      <h5>Prediction</h5>
      <div className="w-100">
      <p>
        For our reservoir predictions, we begin harvesting from our last recorded state.
        Since we seek to continue our series from our data, we use our saved step from the fitting phase as the first step of our prediction phase.
      </p>
      <p>
        We calculate the output value of each state using <code>w_out</code>.
      </p>
      <SyntaxHighlighter className="w-100" language={ "python" }>
      {`
        def predict(self, input, cont=True):
          input = np.reshape(input, (len(input), -1))
          n_count = input.shape[0]
          last = self.last if cont else [np.zeros(self.n_units), np.zeros(self.n_in), np.zeros(self.n_fb)]
          input = np.vstack([last[0], input])
          states = np.vstack([last[1], np.zeros((n_count, self.n_units))])
          output = np.vstack([last[2], np.zeros((n_count, self.n_fb))])
          for n in range(n_count):
            states[n+1] = self._next(states[n], input[n+1], output[n])
            output[n+1] = np.dot(self.w_out, np.concatenate([states[n+1], input[n+1]]))
          return output[1:]
      `}
      </SyntaxHighlighter>
      </div>
    </div>
  );
}

export default Implementation;
